/**
 * React dependencies
 */

import React, { useContext, useState } from 'react';
import { trackBookInfoClick } from '../../analytics';
import AudioPlayer from '../AudioPlayer/AudioPlayer';
import { ModalContext } from '../Modal/ModalContext';
import { handleAudioContributors, handleBookAuthors, handleBookCoverImage, isEncoded } from '../utils/component-utils';
import { BooksContext } from './BooksContext';

/**
 * Book
 *
 * A component to render a single book
 *
 * @param {Object}  props
 * @param {Object}  props.item
 * @param {boolean} props.showMetadata
 */
const Book = React.memo( ({ item, showMetadata }) => {
  const booksContext = useContext( BooksContext );
  const [ context ] = useState( booksContext );
  const { onClickOpenModal } = useContext( ModalContext );

  const getFormat = context?.entry === 'automatic' ? item.representative?.format?.description : item?.format;
  const getCoverImage = handleBookCoverImage( item, context?.entry );
  const getAuthor = decodeURIComponent( handleBookAuthors( item, context?.entry ) );
  const getAudioContributors = handleAudioContributors( item, context?.entry === 'automatic' );
  const getTitle = isEncoded( item?.title ) ? item.title : encodeURIComponent( item.title );

  const allowed = context?.allowedMetadataFields
    ? JSON.parse( context?.allowedMetadataFields )
    : [ 'Title', 'Contributor Name', 'Format' ];
  const altText =
        context?.entry === 'manual' && item?.altTextManual
          ? item.altTextManual
          : `${decodeURIComponent( getTitle )} by ${getAuthor}`;

  const bookCoverStyle = window?.DP?.book_cover_background;
  const isAudio = bookCoverStyle === 'Audio';

  return (
    <div
      className={`book--inner ${
        !isAudio ? 'with-hover' : ''
      }`}
    >
      {isAudio
        ? (
          <>
            <AudioPlayer
              item={item}
              altText={altText}
              format={getFormat}
              coverUrl={getCoverImage}
              context={context}
            />
          </>
        )
        : (
          <>
            <div className={`image-wrapper ${isAudio ? 'is-audio-book' : 'is-regular-book'}`}>
              <div className="api-image-wrapper with-hover">
                <img
                  className="api-image"
                  src={getCoverImage}
                  alt={altText}
                  data-format={getFormat}
                  data-isbn={item?.isbn}
                  data-workid={item?.workId}
                  title={decodeURIComponent( getTitle )}
                />
              </div>
              <div className="overlay absolute bottom-0 left-0 right-0 top-0 bg-gray opacity-0 transition-all duration-300 ease-in-out"></div>
              <button
                aria-label={`View details for ${decodeURIComponent( getTitle )}`}
                className="view-details bg-white px-11 py-17 text-12 uppercase"
                onClick={() =>
                  trackBookInfoClick( item, context ) &&
                  onClickOpenModal( item?.isbn, 'books' )
                }
              >
                + View Details
              </button>
            </div>
          </>
        )}

      { showMetadata && (
        <div className="book-details mt-16">
          { item?.title && allowed.includes( 'Title' ) && (
            <p className="book-details__title paragraph-1 text-16 my-0"><strong>{ decodeURIComponent( getTitle ) }</strong></p>
          ) }
          { allowed.includes( 'Contributor Name' ) && (
            <>
              { isAudio
                ? (
                  <p className="book-details__author paragraph-2 text-[0.9rem] my-0">{ getAudioContributors || getAuthor }</p>
                )
                : (
                  <p className="book-details__author paragraph-2 text-[0.9rem] my-0">{ getAuthor }</p>
                )}
            </>
          )}
          { getFormat && allowed.includes( 'Format' ) && (
            <p className="book-details__format paragraph-2 text-[0.9rem] my-0 text-[#606060]">{ getFormat }</p>
          ) }
        </div>
      ) }
    </div>
  );
});

export default Book;
